<template>
    <v-dialog
        :model-value="modelValue"
        :max-width="1080"
        :persistent="submitting"
        data-testid="add-placement-wizard"
        @update:model-value="onDialogUpdate"
    >
        <div
            class="apw-card"
            role="dialog"
            aria-modal="true"
            data-testid="apw-card"
        >
            <header class="apw-header">
                <h3 class="apw-title">Add placement</h3>
                <button
                    type="button"
                    class="apw-close"
                    aria-label="Close"
                    data-testid="apw-close"
                    :disabled="submitting"
                    @click="onCancel"
                >
                    &times;
                </button>
            </header>

            <!-- =================================================== -->
            <!-- Single-modal body: all fields shown together. The     -->
            <!-- former 5-step stepper collapsed into one scrollable   -->
            <!-- dialog (artwork, method, zone, dimensions) with a     -->
            <!-- single "Add placement" submit. There is no success    -->
            <!-- screen: the parent closes the dialog on `created`.    -->
            <!-- =================================================== -->
            <!-- Two columns (stacked below 800px): the artwork list on   -->
            <!-- the LEFT (first in tab order), every other field on the -->
            <!-- RIGHT, so the dialog fits a laptop screen unscrolled.   -->
            <div class="apw-body" data-testid="apw-body">
                <div class="apw-col apw-col--decos" data-testid="apw-col-decos">
                    <!-- =============================================== -->
                    <!-- Field: Pick artwork (autocomplete + thumbnails) -->
                    <!-- =============================================== -->
                    <section
                        class="apw-section"
                        data-testid="apw-field-artwork"
                    >
                    <p class="apw-help">
                        Pick an artwork from the library. The wizard will
                        find or create the matching Decoration for the
                        method + dimensions you set below.
                    </p>
                    <label class="apw-field">
                        <span class="apw-label">
                            Artwork <span class="apw-req">*</span>
                        </span>
                        <input
                            v-model.trim="artworkQuery"
                            type="text"
                            class="apw-input"
                            placeholder="Type to filter artwork..."
                            data-testid="apw-artwork-search"
                            :disabled="submitting"
                        />
                    </label>
                    <ul
                        v-if="filteredArtworks.length"
                        class="apw-options"
                        role="listbox"
                        data-testid="apw-artwork-options"
                    >
                        <li
                            v-for="art in filteredArtworks"
                            :key="art.id"
                            class="apw-option"
                            :class="{
                                'apw-option--selected':
                                    selectedArtworkId === art.id,
                            }"
                            :data-testid="`apw-artwork-option-${art.id}`"
                            role="option"
                            tabindex="0"
                            @click="selectArtwork(art)"
                            @keyup.enter="selectArtwork(art)"
                            @keyup.space.prevent="selectArtwork(art)"
                        >
                            <!--
                              Same rules as the Decoration Library list
                              (utils/artworkPreviewState.js): "no preview for
                              this file type", a stitch file's render, then the
                              thumbnail, else the source image unless it is a
                              raw vector print file. The letter is the last
                              resort, not the answer for SVG/EPS (Bug Intake #309).
                            -->
                            <span
                                v-if="thumbKind(art) === 'unsupported'"
                                class="apw-thumb apw-thumb--placeholder apw-thumb--note"
                                role="img"
                                :aria-label="`${thumbNote(art).message}. ${thumbNote(art).hint}`"
                                :title="thumbNote(art).hint"
                                :data-testid="`apw-thumb-no-preview-${art.id}`"
                            >
                                No preview
                            </span>
                            <span
                                v-else-if="thumbKind(art) === 'pending'"
                                class="apw-thumb apw-thumb--placeholder apw-thumb--note"
                                role="img"
                                aria-label="Generating preview"
                                :data-testid="`apw-thumb-pending-${art.id}`"
                            >
                                ...
                            </span>
                            <DecorationArtThumb
                                v-else-if="thumbSrc(art)"
                                :src="thumbSrc(art)"
                                :ink-hex="art.dominant_ink_hex"
                                :ink-mixed="art.dominant_ink_mixed === true"
                                :alt="artworkLabel(art)"
                                class="apw-thumb"
                                :data-testid="`apw-thumb-${art.id}`"
                            />
                            <div
                                v-else
                                class="apw-thumb apw-thumb--placeholder"
                                :data-testid="`apw-thumb-placeholder-${art.id}`"
                            >
                                {{ initialOf(art) }}
                            </div>
                            <span class="apw-option__name">
                                {{ artworkLabel(art) }}
                            </span>
                            <span
                                class="apw-option__badge"
                                :data-testid="`apw-used-by-${art.id}`"
                            >
                                used by {{ art.used_by_count || 0 }}
                                {{
                                    (art.used_by_count || 0) === 1
                                        ? 'product'
                                        : 'products'
                                }}
                            </span>
                        </li>
                    </ul>
                    <div
                        v-else-if="!artworksPending"
                        class="apw-empty"
                        data-testid="apw-artwork-empty"
                    >
                        No artworks match.
                    </div>
                    <div
                        v-if="artworksPending"
                        class="apw-loading"
                        data-testid="apw-artwork-loading"
                    >
                        Loading artworks...
                    </div>
                    <div
                        v-if="loadError"
                        class="apw-error"
                        data-testid="apw-artwork-load-error"
                    >
                        Could not load artworks.
                    </div>
                    <!--
                        P5.5 PR-4 (task #196): inline upload affordance.
                        Launches AddArtworkWizard as a child modal; on
                        success the new artwork is appended + auto-
                        selected so the user can continue filling the modal.
                    -->
                    <div class="apw-upload-row">
                        <button
                            type="button"
                            class="apw-btn apw-btn--ghost"
                            data-testid="apw-upload-new-artwork"
                            :disabled="submitting || uploadRepoLoading"
                            @click="openUploadModal"
                        >
                            + Upload new artwork
                        </button>
                        <span
                            v-if="uploadRepoLoading"
                            class="apw-loading"
                            data-testid="apw-upload-repo-loading"
                        >
                            Loading...
                        </span>
                    </div>
                    <p
                        v-if="uploadRepoError"
                        class="apw-error"
                        data-testid="apw-upload-repo-error"
                    >
                        {{ uploadRepoError }}
                    </p>
                    </section>
                </div>

                <div class="apw-col apw-col--fields" data-testid="apw-col-fields">
                    <!-- =============================================== -->
                    <!-- Field: Pick method                              -->
                    <!-- =============================================== -->
                    <section
                        class="apw-section"
                        data-testid="apw-field-method"
                    >
                    <p class="apw-help">
                        Choose the decoration method. The Decoration's
                        digitization is method-specific (embroidery sew
                        file, screen-print color separations, etc.).
                    </p>
                    <!--
                        PR-5 / WA-7c method<->format lock: a .dst/.emb
                        artwork is an embroidery stitch source and can ONLY
                        be decorated by embroidery, so the method is locked.
                        The marker lets tests + the ui-runner assert the
                        lock. The backend guard (POST /api/placements/ ->
                        handle_composite_bind) is the authority; this is UX.
                    -->
                    <p
                        v-if="isMethodLocked"
                        class="apw-lock-note"
                        :data-testid="`apw-method-locked-${lockedMethodSlug}`"
                    >
                        This is an embroidery file, so the method is set to
                        embroidery.
                    </p>
                    <label class="apw-field">
                        <span class="apw-label">
                            Method <span class="apw-req">*</span>
                        </span>
                        <select
                            v-model="selectedMethodId"
                            class="apw-input"
                            data-testid="apw-method"
                            :disabled="submitting || methodsPending || isMethodLocked"
                        >
                            <option :value="null">Choose a method...</option>
                            <option
                                v-for="m in methods"
                                :key="m.id"
                                :value="m.id"
                                :disabled="isMethodOptionDisabled(m)"
                                :data-testid="`apw-method-${m.slug}`"
                            >
                                {{ m.display_name || m.slug }}
                            </option>
                        </select>
                    </label>
                    </section>

                    <!-- =============================================== -->
                    <!-- Field: Pick zone                                -->
                    <!-- =============================================== -->
                    <section
                        class="apw-section"
                        data-testid="apw-field-zone"
                    >
                    <p class="apw-help">
                        Where on the product will this Decoration go?
                    </p>
                    <label class="apw-field">
                        <span class="apw-label">
                            Zone <span class="apw-req">*</span>
                        </span>
                        <select
                            v-model="selectedZoneId"
                            class="apw-input"
                            data-testid="apw-zone"
                            :disabled="submitting || zonesPending"
                        >
                            <option :value="null">Choose a zone...</option>
                            <option
                                v-for="opt in zoneOptions"
                                :key="opt.value"
                                :value="opt.value"
                            >
                                {{ opt.label }}
                            </option>
                        </select>
                    </label>
                    <p
                        v-if="!zonesPending && zoneOptions.length === 0"
                        class="apw-help apw-empty"
                        data-testid="apw-zone-empty"
                    >
                        No decoratable placements are configured for this product
                        in Artifi.
                    </p>
                    </section>

                    <!-- =============================================== -->
                    <!-- Field: Dimensions                               -->
                    <!-- =============================================== -->
                    <section
                        class="apw-section"
                        data-testid="apw-field-dimensions"
                    >
                    <p class="apw-help">
                        Finished imprint size in inches. It starts at the
                        largest size of the artwork that fits the location,
                        and width and height keep the artwork's proportions.
                        The backend reuses an existing Decoration of the
                        same artwork and method within +/- 10% of this size.
                    </p>
                    <div class="apw-row">
                        <label class="apw-field">
                            <span class="apw-label">
                                Width (in) <span class="apw-req">*</span>
                            </span>
                            <input
                                :value="widthIn"
                                type="number"
                                class="apw-input"
                                step="0.01"
                                min="0.01"
                                data-testid="apw-width"
                                :disabled="submitting"
                                @input="onDimInput('width', $event.target.value)"
                                @change="onDimCommit('width', $event.target.value)"
                            />
                        </label>
                        <span
                            v-if="sizeLocked"
                            class="material-icons apw-size-lock"
                            data-testid="apw-size-lock"
                            role="img"
                            aria-label="Proportions locked"
                            title="Width and height keep the artwork's proportions"
                        >lock</span>
                        <span
                            v-else-if="sizeShapeUnknown"
                            class="material-icons apw-size-lock apw-size-lock--unknown"
                            data-testid="apw-size-shape-unknown"
                            role="img"
                            aria-label="Shape unknown: proportions not locked"
                            title="This artwork's shape is not known, so width and height are not locked together"
                        >lock_open</span>
                        <label class="apw-field">
                            <span class="apw-label">
                                Height (in) <span class="apw-req">*</span>
                            </span>
                            <input
                                :value="heightIn"
                                type="number"
                                class="apw-input"
                                step="0.01"
                                min="0.01"
                                data-testid="apw-height"
                                :disabled="submitting"
                                @input="onDimInput('height', $event.target.value)"
                                @change="onDimCommit('height', $event.target.value)"
                            />
                        </label>
                    </div>
                    <p
                        v-if="fileSizeHint"
                        class="apw-help apw-size-hint"
                        data-testid="apw-size-from-file"
                    >
                        Pre-filled: {{ fileSizeHint }} (file size)
                    </p>
                    <p
                        v-if="sizeMeasuring"
                        class="apw-help apw-size-hint"
                        data-testid="apw-size-measuring"
                        role="status"
                    >
                        Measuring file size...
                    </p>
                    <p
                        v-if="sizeUnreadable"
                        class="apw-help apw-size-hint"
                        data-testid="apw-size-unreadable"
                        role="status"
                    >
                        The size of this file could not be read. Enter the
                        size you want, or keep the default.
                    </p>
                    <p
                        v-if="dimensionError"
                        class="apw-error"
                        data-testid="apw-dimension-error"
                    >
                        {{ dimensionError }}
                    </p>
                    <!--
                        Card #573: the low-resolution note. Soft tier and warn
                        mode only inform; below the minimum under "reject" the
                        server refuses the size, so the submit is disabled
                        (submitEnabled) and this says why.
                    -->
                    <LowResolutionNotice :resolution="lowResolution" />
                    <!--
                        Jake, 2026-10-29: nothing is blocked; a low-resolution
                        DTF image may opt into an Artistic Punch cleanup or,
                        with that switched off, is told whom to email.
                    -->
                    <LowResolutionCleanupAction
                        v-if="lowResolution && lowResolution.severity"
                        :resolution="lowResolution"
                        :artwork-id="Number(selectedArtworkId)"
                        :width="Number(widthIn)"
                        :height="Number(heightIn)"
                    />
                    </section>

                    <!-- =============================================== -->
                    <!-- Live compatibility verdict. Runs automatically  -->
                    <!-- once all required fields are valid; shown inline -->
                    <!-- so the user sees the fit result before submit    -->
                    <!-- without a separate review screen.                -->
                    <!-- =============================================== -->
                    <section
                        v-if="compatibilityVerdict || compatibilityChecking"
                        class="apw-section"
                        data-testid="apw-field-review"
                    >
                        <div
                            v-if="compatibilityVerdict"
                            class="apw-verdict"
                            :class="`apw-verdict--${compatibilityVerdict.verdict || 'unknown'}`"
                            data-testid="apw-verdict"
                        >
                            <span class="apw-verdict__label">
                                Compatibility:
                            </span>
                            <span
                                class="apw-verdict__value"
                                :data-testid="`apw-verdict-${compatibilityVerdict.verdict || 'unknown'}`"
                            >
                                {{ verdictLabel(compatibilityVerdict) }}
                            </span>
                        </div>
                        <div
                            v-else-if="compatibilityChecking"
                            class="apw-loading"
                            data-testid="apw-verdict-loading"
                        >
                            Checking compatibility...
                        </div>
                    </section>
                </div>
            </div>

            <p
                v-if="submitError"
                class="apw-error apw-error--submit"
                data-testid="apw-submit-error"
            >
                {{ submitError }}
            </p>

            <!-- =================================================== -->
            <!-- Footer (nav + submit)                                -->
            <!-- =================================================== -->
            <footer class="apw-footer">
                <button
                    type="button"
                    class="apw-btn"
                    data-testid="apw-cancel"
                    :disabled="submitting"
                    @click="onCancel"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="apw-btn apw-btn--primary"
                    data-testid="apw-submit"
                    :disabled="!submitEnabled"
                    @click="onSubmit"
                >
                    {{ submitting ? 'Saving...' : 'Add placement' }}
                </button>
            </footer>
        </div>
        <!--
            P5.5 PR-4 (task #196): inline AddArtworkWizard launched
            from the artwork field's "+ Upload new artwork" button.
            Renders as a sibling dialog so the placement wizard stays
            mounted (preserves the already-entered method / zone / dims
            when the user swaps artwork).
        -->
        <AddArtworkWizard
            v-if="uploadOpen"
            v-model="uploadOpen"
            :repository-id="uploadRepositoryId"
            :distributor-id="uploadRepositoryDistributorId"
            data-testid="apw-inline-upload-wizard"
            @created="onArtworkUploaded"
            @reused="onArtworkUploaded"
        />
    </v-dialog>
</template>

<script>
/**
 * AddPlacementWizard.vue -- product-editor binding flow (P5 PR-D).
 *
 * Card: Boulders #163 (https://trello.com/c/S5kN5poU).
 *
 * Pre-refactor: a 3-step ``AddPlacementModal`` staged decorations
 * directly against the legacy decoration model via the productEditor
 * store. P5 splits the "+ Add placement" affordance from the upload
 * affordance per the locked product decisions:
 *
 *   - AddArtworkWizard (PR-C): upload-only, at the Library list entry
 *     point.
 *   - AddPlacementWizard (this component): bind an existing Artwork to
 *     a product zone + method, at the product-editor entry point.
 *
 * Single modal (polish/add-placement-single-modal): the former 5-step
 * stepper was collapsed into ONE dialog that shows all fields at once in
 * a single scrollable body, with a single "Add placement" submit + Cancel
 * (no Next/Back paging). The fields + their behavior are unchanged:
 *   - PICK ARTWORK -- Vuetify-friendly typedown picker against the
 *      tenant's library. Each option shows a thumbnail + name +
 *      ``used by N products`` badge. Mirrors the
 *      ``DecorationSetPicker`` pattern (Phase 4.8 / P4-Q3).
 *   - PICK METHOD -- dropdown of seeded ``decoration_processes``
 *      (embroidery / screen_print / dtf / htv). A .dst/.emb artwork
 *      locks this to embroidery (PR-5 / WA-7c).
 *   - PICK ZONE -- data-driven (#2214): when the SKU's live Artifi
 *      views are known (from editorUi.skuViews or the supplierSku
 *      fetch) the picker offers THOSE views (label = display_name,
 *      value = code) and submit sends ``view_code`` so the backend
 *      find-or-creates the zone. When views are unknown the picker
 *      fails open to the legacy fixed 8-zone catalog + a
 *      ``placement_zone_id`` submit.
 *   - PICK DIMENSIONS -- numeric width_in + height_in. The backend
 *      uses these for the +/- 10% find_compatible match.
 *   - COMPATIBILITY CHECK -- runs automatically once all fields are
 *      valid (was a discrete "review" step); calls
 *      ``POST /api/placement_compatibility/check_batch/`` (P4.2) and
 *      shows a fit / remap / drop verdict pill inline.
 *   - SUBMIT -- calls
 *      ``POST /api/placements/`` (P5.5 PR-3a one-step bind shape; was
 *      ``POST /api/decorations/find_or_create_and_bind/`` in P5 PR-D,
 *      retired when the SAFRS /api/decorations/ surface was dropped
 *      in P5.5 PR-2c) to mint or re-use the Decoration AND create the
 *      Placement in one transaction. The outcome differs for
 *      auto-bind vs create-new (announced by the parent) per
 *      ``project_p5-ui-design-decisions`` Decision #2:
 *
 *        bound_to_existing=true  -> "Bound to existing Decoration (no new approval)"
 *        bound_to_existing=false -> "Created new Decoration (queued for approval)"
 *
 * Props
 * -----
 *   modelValue          v-model open/close (parent owns visibility).
 *   campaignProductId   the product editor's current CP id. Forwarded
 *                       to the bind endpoint. When null/undefined, the
 *                       wizard surfaces an inline error and short-circuits
 *                       the submit.
 *   preSelectedArtworkId optional -- pre-fills the artwork picker (e.g.,
 *                       launched from the Library snackbar's "Add a
 *                       placement now" affordance).
 *   preSelectedZoneId   optional -- pre-fills the zone picker (P5.5 PR-4,
 *                       task #196). Used by the product-editor zone-aware
 *                       "+Add placement" affordance so the user lands on
 *                       the wizard with the target zone already selected
 *                       (Flow A: place-then-decorate).
 *
 * Emits
 * -----
 *   update:modelValue   open/close v-model contract.
 *   created             { placement_id, decoration_id, bound_to_existing }
 *                       so the product editor can refresh its placements
 *                       list without a second round-trip.
 *
 * Inline upload (P5.5 PR-4, task #196)
 * ------------------------------------
 * The artwork field carries a "+ Upload new artwork" affordance that
 * launches the existing ``AddArtworkWizard`` modal inline. On success the
 * new artwork is appended to the picker list AND auto-selected; the user
 * then fills the remaining fields (method / zone / dimensions) in the same
 * modal. This realises the "inline upload+digitize within the product
 * editor's placement UI" requirement (Jake 2026-06-25) without forcing the
 * user to leave the editor and round-trip through the Decoration Library.
 */
import api from '@/services/api'
import { inPlaceApiError } from '@/utils/safeApiError'
import { SUPPRESS_ERROR_TOAST } from '@/interceptors/authResponseInterceptor'
import AddArtworkWizard from './AddArtworkWizard.vue'
import DecorationArtThumb from './DecorationArtThumb.vue'
import { formatArtworkName } from '@/utils/formatArtworkName'
import {
    artworkImageSrc,
    artworkPreviewDisplay,
    artworkThumbnailSrc,
} from '@/utils/artworkPreviewState'
import { isEmbroiderySource } from '@/utils/artworkUploadContract'
import {
    isZoneValidForSku,
    strictViewCodeForSku,
    defaultDimsForViewCode,
    viewCodeToZoneSlug,
} from '@/utils/placementZoneToViewCode'
import {
    fitToEnvelope,
    sizeFromHeight,
    sizeFromWidth,
} from '@/composables/useAspectLockedSize'
import { zoneOptionsForSku } from '@/utils/placementLocationOptions'
import { useProductEditorUiState } from '@/stores/productEditorUiState'
import LowResolutionNotice from './LowResolutionNotice.vue'
import LowResolutionCleanupAction from './LowResolutionCleanupAction.vue'

// Single-modal consolidation: the wizard is one form (all fields shown
// together). The former 5-step stepper and its success screen are gone; the
// parent announces the outcome after it closes the dialog.
// Re-fetch delays (ms) while an in-form stitch upload is being measured:
// ~1 minute in total, then the form gives up and says so.
const SIZE_POLL_DELAYS_MS = [
    1000, 1500, 2000, 3000, 4000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
    5000, 5000,
]

function _isPositiveNumber(v) {
    return typeof v === 'number' && Number.isFinite(v) && v > 0
}

export default {
    name: 'AddPlacementWizard',

    components: {
        AddArtworkWizard,
        DecorationArtThumb,
        LowResolutionNotice,
        LowResolutionCleanupAction,
    },

    props: {
        modelValue: { type: Boolean, default: false },
        campaignProductId: {
            type: [Number, String],
            default: null,
        },
        preSelectedArtworkId: {
            type: [Number, String],
            default: null,
        },
        /**
         * P5.5 PR-4 (task #196): pre-select a zone so callers
         * launching from a zone-aware affordance (e.g. the product
         * editor's per-zone "+Add placement" button) land on the
         * wizard with the target zone already chosen. The zone field
         * still renders in the single modal; the user can override.
         */
        preSelectedZoneId: {
            type: [Number, String],
            default: null,
        },
        /**
         * The product's supplier/master SKU (e.g. "K540"). When provided,
         * the wizard fetches the product's LIVE Artifi views and offers ONLY
         * zones that map to a real view for this product -- invalid
         * placements (e.g. "Full Center" on a polo that lacks it) are never
         * shown, and a selected-but-invalid zone is hard-blocked on submit.
         * When null, the wizard fails open (offers all zones; the backend
         * substitution net remains the last guard).
         */
        supplierSku: {
            type: String,
            default: null,
        },
    },

    emits: ['update:modelValue', 'created'],

    setup() {
        // Data-driven placement zones (#2214): the wizard sources the
        // SKU's live Artifi views from the shared editor UI store so the
        // picker offers the product's ACTUAL views in every launch
        // context, not only when a ``supplierSku`` prop is passed. Held
        // as a non-reactive-wrapper store handle; ``editorUi.skuViews`` is
        // read reactively inside the computeds below.
        const editorUi = useProductEditorUiState()
        return { editorUi }
    },

    data() {
        return {
            // Picker data
            artworks: [],
            artworksPending: false,
            loadError: false,
            artworkQuery: '',
            selectedArtworkId: null,
            // Method data
            methods: [],
            methodsPending: false,
            selectedMethodId: null,
            // PR-5 / WA-7c: true when the method was auto-forced by the
            // .dst/.emb embroidery lock (vs. chosen by the user). Lets
            // us undo only the forced pick when the lock lifts.
            _methodAutoLocked: false,
            // Zone data
            zones: [],
            zonesPending: false,
            selectedZoneId: null,
            // Live Artifi views for supplierSku (null until fetched / when
            // no SKU / on error -> fail open). Authoritative filter source.
            skuViews: null,
            // Dimensions
            widthIn: null,
            heightIn: null,
            // True once the user has typed in width or height; the
            // pre-fills (file size, zone default) never overwrite it.
            dimsEdited: false,
            // True while widthIn/heightIn hold the file's measured size
            // (so a pick of an unmeasured file can take it back out).
            dimsFromFile: false,
            // In-form upload of a stitch file: its size is unknown until
            // the preview render finishes, so the form re-fetches the
            // artwork until ``measured_size_in`` arrives. 'idle' |
            // 'measuring' | 'unreadable', for ``sizeMeasureArtworkId``.
            sizeMeasureArtworkId: null,
            sizeMeasureState: 'idle',
            dimensionError: null,
            // Low-resolution verdict for the chosen artwork, method and size
            // (card #573): {dpi, threshold, low} from the server, or null.
            // A WARNING only: nothing reads it to gate the submit.
            lowResolution: null,
            resolutionSeq: 0,
            resolutionScheduled: false,
            // Compatibility verdict
            compatibilityVerdict: null,
            compatibilityChecking: false,
            // Coalesces a burst of field changes into one compat check.
            _compatCheckScheduled: false,
            // Submission
            submitting: false,
            submitError: null,
            // P5.5 PR-4: inline artwork upload modal
            uploadOpen: false,
            uploadRepositoryId: null,
            uploadRepositoryDistributorId: null,
            uploadRepoLoading: false,
            uploadRepoError: null,
        }
    },

    computed: {
        filteredArtworks() {
            const q = (this.artworkQuery || '').trim().toLowerCase()
            const all = Array.isArray(this.artworks) ? this.artworks : []
            if (!q) return all
            return all.filter((a) => {
                const name = this.artworkLabel(a).toLowerCase()
                return name.includes(q)
            })
        },
        // Single combined submit gate: all four fields must be valid at
        // once (artwork + method + zone + positive width/height + no
        // dimension error). This is the former per-step nextEnabled rules
        // folded into one guard now that every field is on screen together.
        submitEnabled() {
            return (
                !this.submitting &&
                this.selectedArtworkId != null &&
                this.selectedMethodId != null &&
                this.selectedZoneId != null &&
                _isPositiveNumber(this.widthIn) &&
                _isPositiveNumber(this.heightIn) &&
                !this.dimensionError &&
                !this.resolutionBlocked
            )
        },
        // The server says this artwork at this size is below the method's
        // minimum DPI and the method refuses that (card #573).
        resolutionBlocked() {
            return Boolean(this.lowResolution && this.lowResolution.blocked)
        },
        // Composite identity of the compatibility-check inputs, including
        // the fields that GATE validity (method) so a single watcher on
        // this key fires the check exactly once when the form first becomes
        // valid AND re-fires when any verdict-affecting value changes on an
        // already-valid form. Keying on the whole tuple (rather than two
        // separate watchers) avoids a double-fire that would consume a
        // mocked single-shot response and blank the verdict.
        _compatKey() {
            return [
                this.selectedArtworkId,
                this.selectedMethodId,
                this.selectedZoneId,
                this.widthIn,
                this.heightIn,
                this.campaignProductId,
            ].join('|')
        },
        // Inputs of the low-resolution read: it needs the artwork, the method
        // and a size, but not a location.
        _resolutionKey() {
            if (
                this.selectedArtworkId == null ||
                this.selectedMethodId == null ||
                !_isPositiveNumber(Number(this.widthIn)) ||
                !_isPositiveNumber(Number(this.heightIn))
            ) {
                return ''
            }
            return [
                this.selectedArtworkId,
                this.selectedMethodId,
                Number(this.widthIn),
                Number(this.heightIn),
            ].join('|')
        },
        // True when every required field is valid (mirrors submitEnabled
        // minus the submitting guard). Drives the automatic compatibility
        // check now that there is no explicit "advance to review" step.
        allFieldsValid() {
            return (
                this.selectedArtworkId != null &&
                this.selectedMethodId != null &&
                this.selectedZoneId != null &&
                _isPositiveNumber(this.widthIn) &&
                _isPositiveNumber(this.heightIn) &&
                !this.dimensionError
            )
        },
        selectedArtwork() {
            if (this.selectedArtworkId == null) return null
            return (
                this.artworks.find(
                    (a) => String(a.id) === String(this.selectedArtworkId),
                ) || null
            )
        },
        // The size (inches) the preview drain measured from the selected
        // stitch file, or null when unknown (raster, not rendered yet, no
        // size from the render). Served as ``measured_size_in`` on the
        // library row, already validated server-side (finite, positive).
        measuredSize() {
            const size = this.selectedArtwork?.measured_size_in
            if (!size) return null
            const w = Number(size.w)
            const h = Number(size.h)
            return _isPositiveNumber(w) && _isPositiveNumber(h)
                ? { w, h }
                : null
        },
        // "4.48 x 1.45 in" while the fields still hold the file's size.
        fileSizeHint() {
            if (!this.dimsFromFile || this.dimsEdited || !this.measuredSize) {
                return ''
            }
            const fmt = (n) => String(Number(n.toFixed(2)))
            return `${fmt(this.measuredSize.w)} x ${fmt(this.measuredSize.h)} in`
        },
        sizeMeasuring() {
            return (
                this.sizeMeasureState === 'measuring' &&
                this._sizeMeasureIsSelected() &&
                !this.dimsEdited &&
                !this.measuredSize
            )
        },
        sizeUnreadable() {
            return (
                this.sizeMeasureState === 'unreadable' &&
                this._sizeMeasureIsSelected() &&
                !this.measuredSize
            )
        },
        selectedMethod() {
            if (this.selectedMethodId == null) return null
            return (
                this.methods.find(
                    (m) => String(m.id) === String(this.selectedMethodId),
                ) || null
            )
        },
        // The SKU's live Artifi views. Prefer the shared editor store
        // (populated by Stage.vue via setSkuViews in every editor
        // context); fall back to the wizard's own prop-driven fetch
        // (``this.skuViews``) when the store is empty. A non-empty array
        // is authoritative; anything else means "no authority -> fail
        // open to the legacy fixed catalog".
        effectiveSkuViews() {
            const fromStore = this.editorUi ? this.editorUi.skuViews : null
            if (Array.isArray(fromStore) && fromStore.length) return fromStore
            if (Array.isArray(this.skuViews) && this.skuViews.length) {
                return this.skuViews
            }
            return null
        },
        // Data-driven mode: when the SKU's views are known, the picker
        // offers THOSE (each view is a placement location) and submit
        // sends the chosen ``view_code``. When unknown, we fail open to
        // the legacy fixed-catalog picker + ``placement_zone_id`` submit.
        usingViewCodes() {
            return Array.isArray(this.effectiveSkuViews)
        },
        // The picker's options, normalized to ``{ value, label }``.
        //   - view-code mode: value = view.code, label = view.display_name
        //     (or the code when a display_name is absent).
        //   - legacy mode: value = zone.id, label = zone.display_name,
        //     filtered by isZoneValidForSku (fail-open when no SKU views).
        //   Single-sourced in utils/placementLocationOptions, which the
        //   placement location picker reads too, so a new placement and a
        //   moved one are offered the same locations.
        zoneOptions() {
            return zoneOptionsForSku(this.effectiveSkuViews, this.zones)
        },
        // PR-5 / WA-7c: when the Step-1 artwork is a .dst/.emb stitch
        // source, the method is locked to embroidery (a stitch file can
        // only be embroidery). null = unconstrained. Keyed by SLUG; the
        // signal is the artwork's original filename extension via the
        // shared isEmbroiderySource() helper (single source of truth --
        // same one the backend guard mirrors). The library payload's
        // original_filename carries the real uploaded name; filename is
        // the fallback for legacy rows.
        lockedMethodSlug() {
            const art = this.selectedArtwork
            if (!art) return null
            const name = art.original_filename || art.filename
            return isEmbroiderySource(name) ? 'embroidery' : null
        },
        isMethodLocked() {
            return Boolean(this.lockedMethodSlug)
        },
        selectedZone() {
            if (this.selectedZoneId == null) return null
            // In legacy mode the selection is a numeric zone id; resolve
            // the catalog row so the dimension pre-fill + compatibility
            // check can read its envelope. In view-code mode there is no
            // catalog row (the zone is find-or-created server-side).
            if (this.usingViewCodes) return null
            return (
                this.zones.find(
                    (z) => String(z.id) === String(this.selectedZoneId),
                ) || null
            )
        },
        // The chosen location's envelope (inches): the catalog row's
        // default max. In view-code mode, the zone row the bind will find
        // for that view (view_code_to_zone_slug: "Center Back" is
        // full_back), else the envelope a minted zone gets
        // (defaultDimsForViewCode). One source with the backend's default
        // size for a new placement.
        zoneEnvelope() {
            if (this.selectedZoneId == null) return null
            let row = this.selectedZone
            if (this.usingViewCodes) {
                const slug = viewCodeToZoneSlug(String(this.selectedZoneId))
                row = this.zones.find((z) => z.slug === slug) || null
                if (!row) {
                    const dims = defaultDimsForViewCode(String(this.selectedZoneId))
                    return { width: dims.widthIn, height: dims.heightIn }
                }
            }
            if (!row) return null
            const width = Number(row.default_max_width_in)
            const height = Number(row.default_max_height_in)
            if (!_isPositiveNumber(width) || !_isPositiveNumber(height)) return null
            return { width, height }
        },
        // The artwork's shape (width / height) from the library row; null
        // when unknown. A stitch file is exempt: it is sewn at its own size,
        // so its fields are not locked to anything.
        artworkAspect() {
            if (this.isMethodLocked) return null
            const a = Number(this.selectedArtwork?.aspect_ratio)
            return _isPositiveNumber(a) ? a : null
        },
        // Jake, 2026-10-27: the aspect lock is "always on, shown as a
        // visible lock icon with no checkbox" -- whenever the shape is known.
        sizeLocked() {
            return this.artworkAspect != null
        },
        // An artwork is chosen, it is not a stitch file, and its shape is
        // unknown: show the sides are NOT locked (muted) instead of nothing.
        sizeShapeUnknown() {
            return Boolean(this.selectedArtwork) && !this.isMethodLocked && this.artworkAspect == null
        },
        // Back-compat alias kept for any external reader; the picker now
        // renders ``zoneOptions`` directly.
        validZones() {
            return this.zones.filter((z) =>
                isZoneValidForSku(z.slug, this.skuViews),
            )
        },
    },

    watch: {
        modelValue(open) {
            if (open) {
                this._resetState()
                this._preselectFromProp()
                this._hydrate()
            } else {
                this._stopSizeMeasure()
            }
        },
        // PR-5 / WA-7c: keep the method coherent with the artwork
        // format. When the lock engages (a .dst/.emb artwork is chosen)
        // auto-select embroidery; when it disengages (the user swaps to
        // a raster artwork) clear the forced embroidery pick so the
        // method chooser is unconstrained again. Also re-applies once
        // methods load (mount order is not guaranteed).
        lockedMethodSlug() {
            this._applyMethodLock()
        },
        methods() {
            this._applyMethodLock()
        },
        // Prefill sensible dimensions the first time a zone is chosen. In
        // the old stepper this fired on the zone->dimensions transition;
        // now that every field is on screen at once we key it off the zone
        // selection directly. The user can still override (we only fill
        // blanks). Zones changing after that never clobber a user entry.
        // Jake, 2026-10-27: a location change sets the size to the new
        // location's max (even over a size the user typed); only a stitch
        // file's measured size outranks it.
        selectedZoneId() {
            this._prefillDimsForZone({ reset: true })
        },
        // A different shape (another artwork picked) re-fits a size the
        // user has not typed.
        artworkAspect() {
            if (!this.dimsEdited && !this.dimsFromFile) {
                this._prefillDimsForZone({ reset: true })
            }
        },
        // Jake, 2026-10-15: a stitch file has one sewn size, and the
        // "already approved" attestation only applies at that size, so the
        // file's measured size outranks the zone default. Fires when the
        // artwork is picked, and again when a preselected artwork's
        // library row arrives.
        measuredSize() {
            this._prefillDimsForArtwork()
        },
        // Card #573: re-ask the server whether the chosen raster is soft at
        // the chosen size whenever the artwork, method or size changes.
        _resolutionKey() {
            // The verdict belongs to the OLD artwork/method/size: drop it now
            // rather than leave it up until the new answer lands.
            this.lowResolution = null
            this._scheduleResolutionCheck()
        },
        // Auto-run the compatibility check reactively. In the old stepper
        // this fired on entry to the review step; with all fields on screen
        // we key it off the composite input identity so it fires exactly
        // once when the form first becomes valid and re-fires when a
        // verdict-affecting value changes. When the form is not (yet) valid
        // we drop any stale verdict rather than check.
        _compatKey() {
            if (this.allFieldsValid) {
                this._scheduleCompatibilityCheck()
            } else {
                this.compatibilityVerdict = null
            }
        },
    },

    mounted() {
        if (this.modelValue) {
            this._preselectFromProp()
            this._hydrate()
        }
    },

    beforeUnmount() {
        this._stopSizeMeasure()
    },

    methods: {
        _resetState() {
            this.artworkQuery = ''
            this.selectedArtworkId = null
            this.selectedMethodId = null
            this._methodAutoLocked = false
            this.selectedZoneId = null
            this.widthIn = null
            this.heightIn = null
            this.dimsEdited = false
            this.dimsFromFile = false
            this._stopSizeMeasure()
            this.dimensionError = null
            this.compatibilityVerdict = null
            this.compatibilityChecking = false
            this.lowResolution = null
            this.resolutionSeq += 1
            this.submitError = null
            this.submitting = false
            // PR-4 inline upload state
            this.uploadOpen = false
            this.uploadRepoError = null
        },

        _preselectFromProp() {
            // Library-launched flow forwards a preSelectedArtworkId.
            // The picker still hydrates (so the user can change),
            // but the artwork selection is pre-populated so that field
            // is already satisfied on open.
            if (this.preSelectedArtworkId != null) {
                this.selectedArtworkId = this.preSelectedArtworkId
            }
            // P5.5 PR-4: zone-aware launchers (product editor's
            // per-zone "+Add placement" button) forward a
            // preSelectedZoneId. The picker still hydrates so the
            // user can change; the selection is just pre-populated.
            if (this.preSelectedZoneId != null) {
                this.selectedZoneId = this.preSelectedZoneId
            }
        },

        async _hydrate() {
            // Fan out artwork + method + zone loads in parallel.
            await Promise.all([
                this._loadArtworks(),
                this._loadMethods(),
                this._loadZones(),
            ])
        },

        async _loadArtworks() {
            this.artworksPending = true
            this.loadError = false
            try {
                const resp = await api.get('/artworks/library')
                const body = (resp && resp.data) || {}
                this.artworks = Array.isArray(body.artworks)
                    ? body.artworks
                    : []
            } catch (_err) {
                this.artworks = []
                this.loadError = true
            } finally {
                this.artworksPending = false
            }
        },

        async _loadMethods() {
            this.methodsPending = true
            try {
                const resp = await api.get('/decoration_processes/')
                const body = (resp && resp.data) || {}
                this.methods = Array.isArray(body.decoration_processes)
                    ? body.decoration_processes
                    : []
            } catch (_err) {
                this.methods = []
            } finally {
                this.methodsPending = false
            }
        },

        async _loadZones() {
            this.zonesPending = true
            try {
                // Load the zone catalog + this product's live Artifi views in
                // parallel. The views authoritatively filter which zones are
                // offered (validZones). Views are best-effort: on any failure
                // skuViews stays null and the picker fails open.
                const [zonesResp] = await Promise.all([
                    api.get('/placement_zones/'),
                    this._loadSkuViews(),
                ])
                const body = (zonesResp && zonesResp.data) || {}
                this.zones = Array.isArray(body.data) ? body.data : []
            } catch (_err) {
                this.zones = []
            } finally {
                this.zonesPending = false
            }
        },

        async _loadSkuViews() {
            this.skuViews = null
            if (!this.supplierSku) return
            try {
                const resp = await api.get('/artifi/product_views', {
                    params: { sku: this.supplierSku },
                })
                const views = resp?.data?.views
                // Only treat a non-empty list as authoritative; an empty list
                // (or error) fails open via isZoneValidForSku.
                this.skuViews =
                    Array.isArray(views) && views.length ? views : null
            } catch (_err) {
                this.skuViews = null
            }
        },

        artworkLabel(art) {
            if (!art) return ''
            // Strip the '#<guid>' identity suffix for display via the
            // shared formatter (Jake 2026-07-10). The `Artwork #<id>`
            // fallback has no '#'-guid form of its own.
            const raw = art.original_filename || art.filename
            return formatArtworkName(raw) || `Artwork #${art.id}`
        },

        // Bug Intake #309: one answer per row, from the library list's rules.
        thumbNote(art) {
            return artworkPreviewDisplay(art)
        },

        thumbKind(art) {
            const kind = artworkPreviewDisplay(art).kind
            // 'ready' (a stitch file's render) is drawn by thumbSrc, and an
            // unsupported row that has a PNG stand-in is drawn too.
            if (kind === 'unsupported' && artworkThumbnailSrc(art)) return 'none'
            return kind
        },

        // The shared tile-image rule (utils/artworkPreviewState.js).
        thumbSrc(art) {
            return artworkImageSrc(art)
        },

        initialOf(art) {
            const name = this.artworkLabel(art)
            const first = String(name || '').trim().charAt(0)
            return first ? first.toUpperCase() : 'A'
        },

        selectArtwork(art) {
            if (!art) return
            this.selectedArtworkId = art.id
        },

        // PR-5 / WA-7c: when locked, every method EXCEPT the locked one
        // is disabled in the method select.
        isMethodOptionDisabled(method) {
            return this.isMethodLocked && method.slug !== this.lockedMethodSlug
        },

        _applyMethodLock() {
            const slug = this.lockedMethodSlug
            if (slug) {
                // Auto-select the locked method once it is resolvable
                // from the loaded list. Guard the assignment so we do
                // not thrash when it is already correct. Remember that
                // WE forced it so we can undo it if the lock lifts.
                const locked = this.methods.find((m) => m.slug === slug)
                if (locked && this.selectedMethodId !== locked.id) {
                    this.selectedMethodId = locked.id
                }
                this._methodAutoLocked = true
            } else if (this._methodAutoLocked) {
                // Lock disengaged (user swapped a .dst/.emb artwork for a
                // raster one): clear ONLY the pick we forced, so the
                // method chooser starts unconstrained. A pick the user
                // made themselves is never touched.
                this.selectedMethodId = null
                this._methodAutoLocked = false
            }
        },

        // Pre-fill width / height from the selected stitch file's measured
        // size (rotation irrelevant: the file's w x h as measured). Never
        // overwrites a size the user typed. When the pick moves to a file
        // with no known size, take back only what WE filled and fall back
        // to the zone default.
        _prefillDimsForArtwork() {
            if (this.dimsEdited) return
            const size = this.measuredSize
            if (size) {
                this.widthIn = size.w
                this.heightIn = size.h
                this.dimsFromFile = true
            } else if (this.dimsFromFile) {
                this.widthIn = null
                this.heightIn = null
                this.dimsFromFile = false
                this._prefillDimsForZone()
            }
        },

        // Fill width / height with the chosen location's default size: the
        // largest size of the artwork's shape inside the location's envelope
        // (fitToEnvelope; the backend's default size for a new placement).
        // ``reset`` (a location or shape change) replaces a size the user
        // typed (Jake, 2026-10-27); without it only blanks are filled. A
        // stitch file's measured size is never replaced.
        _prefillDimsForZone({ reset = false } = {}) {
            if (this.selectedZoneId == null) return
            if (this.dimsFromFile) return
            const env = this.zoneEnvelope
            if (!env) return
            const fit = fitToEnvelope(this.artworkAspect, env.width, env.height)
            if (!fit) return
            if (reset) {
                this.widthIn = fit.width
                this.heightIn = fit.height
                this.dimsEdited = false
                return
            }
            if (this.widthIn == null) this.widthIn = fit.width
            if (this.heightIn == null) this.heightIn = fit.height
        },

        _lockOpts(clamp) {
            const env = this.zoneEnvelope
            return {
                aspect: this.artworkAspect,
                maxWidth: env ? env.width : null,
                maxHeight: env ? env.height : null,
                clamp,
            }
        },

        // Typing: the other side follows the shape (unclamped while typing).
        // Without a known shape a side is independent, as before.
        onDimInput(side, raw) {
            this.dimsEdited = true
            const n = raw === '' || raw == null ? null : Number(raw)
            if (this.sizeLocked && _isPositiveNumber(n)) {
                const size = side === 'width'
                    ? sizeFromWidth(n, this._lockOpts(false))
                    : sizeFromHeight(n, this._lockOpts(false))
                if (size) {
                    this.widthIn = size.width
                    this.heightIn = size.height
                    return
                }
            }
            if (side === 'width') this.widthIn = n
            else this.heightIn = n
        },

        // Committing (blur / Enter): clamp a locked size into the location,
        // keeping the shape.
        onDimCommit(side, raw) {
            const n = raw === '' || raw == null ? null : Number(raw)
            if (!this.sizeLocked || !_isPositiveNumber(n)) return
            const size = side === 'width'
                ? sizeFromWidth(n, this._lockOpts(true))
                : sizeFromHeight(n, this._lockOpts(true))
            if (size) {
                this.widthIn = size.width
                this.heightIn = size.height
            }
        },

        // Coalesce a burst of synchronous field changes (e.g. choosing a
        // zone that prefills dims, then the user overriding width then
        // height) into a SINGLE compatibility check on the next tick. This
        // both spares the backend a request per keystroke and keeps the
        // verdict coherent with the final field values rather than an
        // intermediate one.
        _scheduleCompatibilityCheck() {
            if (this._compatCheckScheduled) return
            this._compatCheckScheduled = true
            this.$nextTick(() => {
                this._compatCheckScheduled = false
                if (this.allFieldsValid) {
                    this._runCompatibilityCheck()
                }
            })
        },

        // Coalesce a burst of changes into ONE read on the next tick (the
        // compatibility check's pattern).
        _scheduleResolutionCheck() {
            if (!this._resolutionKey) {
                this.resolutionSeq += 1
                this.lowResolution = null
                return
            }
            if (this.resolutionScheduled) return
            this.resolutionScheduled = true
            this.$nextTick(() => {
                this.resolutionScheduled = false
                this._runResolutionCheck()
            })
        },

        // Card #573: the SERVER decides (one rule for every surface). Fails
        // open: a failed read is no warning, never a block. An answer for an
        // earlier choice is dropped.
        async _runResolutionCheck() {
            if (!this._resolutionKey) {
                this.lowResolution = null
                return
            }
            const seq = ++this.resolutionSeq
            const artworkId = this.selectedArtworkId
            try {
                const resp = await api.get(`/artworks/${artworkId}/resolution`, {
                    params: {
                        method_id: Number(this.selectedMethodId),
                        width_in: Number(this.widthIn),
                        height_in: Number(this.heightIn),
                    },
                })
                if (seq !== this.resolutionSeq) return
                this.lowResolution = (resp && resp.data && resp.data.resolution) || null
            } catch (_err) {
                if (seq !== this.resolutionSeq) return
                this.lowResolution = null
            }
        },

        async _runCompatibilityCheck() {
            // The compatibility check keys on a persisted
            // ``placement_zone_id`` (the fit math reads the zone's max
            // envelope). In data-driven view-code mode the zone is
            // find-or-created server-side on submit and has no id yet, so
            // there is nothing to check against -- skip cleanly (the
            // verdict pill simply does not appear). Legacy fixed-
            // catalog mode still runs the check.
            if (this.usingViewCodes) {
                this.compatibilityVerdict = null
                return
            }
            if (
                this.selectedArtworkId == null ||
                this.selectedZoneId == null ||
                !_isPositiveNumber(this.widthIn) ||
                !_isPositiveNumber(this.heightIn)
            ) {
                return
            }
            this.compatibilityChecking = true
            this.compatibilityVerdict = null
            try {
                // ADR 0065: pass the product we're placing on so the
                // verdict shrinks the zone by the garment's size-class
                // scale (youth approx 0.78). Without it the endpoint
                // returns an adult-only verdict, diverging from the
                // Decorations preview + apply surfaces. Omitted when
                // there is no product context (backward compatible: the
                // backend treats a missing campaign_product_id as adult).
                const item = {
                    artwork_id: this.selectedArtworkId,
                    placement_zone_id: this.selectedZoneId,
                    source_imprint_w_in: this.widthIn,
                    source_imprint_h_in: this.heightIn,
                }
                if (this.campaignProductId != null) {
                    item.campaign_product_id = Number(this.campaignProductId)
                }
                const resp = await api.post(
                    '/placement_compatibility/check_batch/',
                    { items: [item] },
                )
                const body = (resp && resp.data) || {}
                const verdicts = Array.isArray(body.verdicts)
                    ? body.verdicts
                    : []
                this.compatibilityVerdict = verdicts[0] || {
                    verdict: null,
                    reason: 'No verdict returned.',
                }
            } catch (_err) {
                this.compatibilityVerdict = {
                    verdict: null,
                    reason: 'Compatibility check failed.',
                }
            } finally {
                this.compatibilityChecking = false
            }
        },

        verdictLabel(v) {
            if (!v) return ''
            if (v.verdict === 'fit') return 'Fits the zone.'
            if (v.verdict === 'remap') {
                return 'Will be resized to fit the zone.'
            }
            if (v.verdict === 'drop') {
                return v.reason || 'Will not fit (too large).'
            }
            return v.reason || 'Unable to determine compatibility.'
        },

        async onSubmit() {
            if (!this.submitEnabled) return
            if (this.campaignProductId == null) {
                this.submitError =
                    'No product context. The wizard needs a campaign ' +
                    'product to bind against.'
                return
            }
            // Legacy fixed-catalog mode hard-block: never create a
            // placement whose zone has no valid Artifi view for this
            // product. The picker already filters these out, but a pre-
            // selected/stale zone could still reach here. This guard is
            // ONLY for legacy mode -- in data-driven view-code mode the
            // selected value IS one of the SKU's real view codes by
            // construction, so there is nothing to block.
            if (!this.usingViewCodes) {
                const zone = this.selectedZone
                if (
                    zone &&
                    strictViewCodeForSku(zone.slug, this.skuViews) === null
                ) {
                    this.submitError =
                        `"${zone.display_name}" is not a decoratable ` +
                        'location for this product in Artifi. Choose a ' +
                        'different placement.'
                    return
                }
            }
            this.submitting = true
            this.submitError = null
            try {
                // P5.5 PR-3a/3b: one-step bind endpoint now mounted
                // at ``/api/placements/`` (was
                // ``/api/decorations/find_or_create_and_bind/`` in
                // P5 PR-D, retired with the SAFRS legacy surface in
                // P5.5 PR-2c). Body + response shape are unchanged
                // -- placement_service.bind handles the find-or-
                // create + bind transaction the same way.
                const payload = {
                    artwork_id: Number(this.selectedArtworkId),
                    method_id: Number(this.selectedMethodId),
                    width_in: Number(this.widthIn),
                    height_in: Number(this.heightIn),
                    campaign_product_id: Number(this.campaignProductId),
                }
                if (this.usingViewCodes) {
                    // Data-driven (#2214): send the chosen Artifi view
                    // code; the backend find-or-creates the zone by its
                    // slugified code (placement_service.bind).
                    payload.view_code = String(this.selectedZoneId)
                } else {
                    // Legacy fixed-catalog: the selection is a persisted
                    // placement_zone_id.
                    payload.placement_zone_id = Number(this.selectedZoneId)
                }
                // This request answers its own errors in place
                // (apw-submit-error), so the app toast is suppressed:
                // one error, where the user is looking.
                const resp = await api.post('/placements/', payload, {
                    [SUPPRESS_ERROR_TOAST]: true,
                })
                const body = (resp && resp.data) || {}
                this.$emit('created', {
                    placement_id:
                        body.placement && body.placement.id != null
                            ? body.placement.id
                            : null,
                    decoration_id:
                        body.placement &&
                        body.placement.decoration_id != null
                            ? body.placement.decoration_id
                            : null,
                    bound_to_existing: Boolean(body.bound_to_existing),
                })
            } catch (err) {
                // The app toast is suppressed for this request, so the
                // inline error carries what the toast would have: a 4xx
                // body message (e.g. size_aspect_mismatch, "should be
                // about 2.39 in tall"), the fixed safeApiError labels for
                // 401/403/404, and never a 5xx body (PR-3b post-review).
                this.submitError = inPlaceApiError(
                    err,
                    'Could not add the placement. Try again.',
                )
            } finally {
                this.submitting = false
            }
        },

        onCancel() {
            if (this.submitting) return
            this.$emit('update:modelValue', false)
        },

        onDialogUpdate(open) {
            if (this.submitting) return
            this.$emit('update:modelValue', open)
        },

        // -----------------------------------------------------------------
        // Inline artwork upload (P5.5 PR-4, task #196)
        // -----------------------------------------------------------------
        //
        // The "+ Upload new artwork" button in the artwork field launches
        // the existing AddArtworkWizard inline. Repository id is resolved
        // lazily on first click (mirrors DecorationLibraryList's
        // pattern). On successful upload the new artwork is appended
        // to the picker list AND auto-selected so the artwork field is
        // immediately satisfied.

        async openUploadModal() {
            if (this.uploadRepositoryId == null) {
                this.uploadRepoLoading = true
                this.uploadRepoError = null
                try {
                    const resp = await api.get('/artwork_repositories/mine')
                    const body = (resp && resp.data) || {}
                    // Backend returns the list under ``repositories`` (see
                    // api/src/endpoints/artworks.py _repositories_mine). The
                    // canonical store (stores/artworks.js) reads the same
                    // key; this wizard previously read ``artwork_repositories``
                    // and so always saw an empty list -> false "No artwork
                    // repository for this tenant" error.
                    const repos = Array.isArray(body.repositories)
                        ? body.repositories
                        : []
                    if (repos.length === 0 || !repos[0].id) {
                        this.uploadRepoError =
                            'No artwork repository for this tenant. ' +
                            'Contact support.'
                        return
                    }
                    this.uploadRepositoryId = repos[0].id
                    this.uploadRepositoryDistributorId =
                        repos[0].distributor_id ?? null
                } catch (_err) {
                    this.uploadRepoError =
                        'Could not load the artwork repository. ' +
                        'Try again.'
                    return
                } finally {
                    this.uploadRepoLoading = false
                }
            }
            this.uploadOpen = true
        },

        onArtworkUploaded(newArtwork) {
            // AddArtworkWizard emits `created` with the new artwork
            // payload AND emits `update:modelValue=false` to close
            // itself. Append to our list + auto-select so the artwork
            // field is satisfied immediately.
            if (newArtwork && newArtwork.id != null) {
                // De-dup defensively (re-uploading the same row
                // through the wizard would otherwise add a phantom).
                const existing = this.artworks.find(
                    (a) => String(a.id) === String(newArtwork.id),
                )
                if (!existing) {
                    this.artworks = [newArtwork, ...this.artworks]
                }
                this.selectedArtworkId = newArtwork.id
                this._startSizeMeasure(newArtwork)
            }
            this.uploadOpen = false
        },

        // -----------------------------------------------------------------
        // Size of a stitch file uploaded from within this form
        // -----------------------------------------------------------------
        //
        // The artwork list is fetched once when the form opens, and the
        // size the preview drain measures lands seconds AFTER the upload.
        // Without a re-fetch the new row keeps ``measured_size_in: null``
        // and the placement is saved at the generic default (placement 54,
        // 2026-10-22: 4 x 4 for a 3.02 x 1.09 file). So poll the library
        // with a bounded backoff; the ``measuredSize`` watcher then
        // pre-fills (never over a size the user typed). Past the cap the
        // form says the size could not be read and keeps its default.

        _sizeMeasureIsSelected() {
            return (
                this.sizeMeasureArtworkId != null &&
                String(this.sizeMeasureArtworkId) ===
                    String(this.selectedArtworkId)
            )
        },

        _startSizeMeasure(art) {
            this._stopSizeMeasure()
            if (!isEmbroiderySource(art.original_filename || art.filename)) {
                return
            }
            const size = art.measured_size_in
            if (size && _isPositiveNumber(Number(size.w)) &&
                _isPositiveNumber(Number(size.h))) {
                return
            }
            this.sizeMeasureArtworkId = art.id
            this.sizeMeasureState = 'measuring'
            this._scheduleSizePoll(0)
        },

        _scheduleSizePoll(attempt) {
            if (attempt >= SIZE_POLL_DELAYS_MS.length) {
                this.sizeMeasureState = 'unreadable'
                this._sizeTimer = null
                return
            }
            this._sizeTimer = setTimeout(
                () => this._pollSize(attempt),
                SIZE_POLL_DELAYS_MS[attempt],
            )
        },

        async _pollSize(attempt) {
            const artworkId = this.sizeMeasureArtworkId
            this._sizeTimer = null
            if (artworkId == null || this.dimsEdited) {
                // The user typed a size: nothing left to pre-fill.
                this._stopSizeMeasure()
                return
            }
            let row = null
            try {
                const resp = await api.get('/artworks/library')
                const list = (resp && resp.data && resp.data.artworks) || []
                row = list.find((a) => String(a.id) === String(artworkId))
            } catch (_err) {
                row = null
            }
            // The form may have been reset/closed while the fetch ran.
            if (this.sizeMeasureArtworkId !== artworkId) return
            if (row) {
                this.artworks = this.artworks.map((a) =>
                    String(a.id) === String(artworkId) ? { ...a, ...row } : a,
                )
                const size = row.measured_size_in
                if (size && _isPositiveNumber(Number(size.w)) &&
                    _isPositiveNumber(Number(size.h))) {
                    // The measuredSize watcher pre-fills from here.
                    this.sizeMeasureState = 'idle'
                    return
                }
                if (row.preview && row.preview.state === 'failed_permanent') {
                    this.sizeMeasureState = 'unreadable'
                    return
                }
            }
            this._scheduleSizePoll(attempt + 1)
        },

        _stopSizeMeasure() {
            if (this._sizeTimer != null) clearTimeout(this._sizeTimer)
            this._sizeTimer = null
            this.sizeMeasureArtworkId = null
            this.sizeMeasureState = 'idle'
        },
    },
}
</script>

<style scoped>
.apw-card {
    background: var(--tf-bg, #fff);
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
    max-height: 92vh;
    /* Header and footer are pinned; the body is the one scroll region. */
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.apw-header,
.apw-footer,
.apw-error--submit {
    flex: none;
}

.apw-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.apw-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    flex: 1 1 auto;
}

.apw-close {
    background: none;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.6);
}

.apw-close:disabled {
    cursor: not-allowed;
    opacity: 0.4;
}

.apw-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 20px;
    display: grid;
    grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
    gap: 24px;
    align-items: start;
}

.apw-col {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
}

/* Below ~800px the columns stack, artwork list first. */
@media (max-width: 800px) {
    .apw-body {
        grid-template-columns: minmax(0, 1fr);
        gap: 16px;
    }
}

/*
 * Single-modal field section. Each of the four fields (artwork, method,
 * zone, dimensions) is a section stacked in the one scrollable body;
 * a light divider between them keeps the long form scannable.
 */
.apw-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.apw-section:last-child {
    border-bottom: 0;
    padding-bottom: 0;
}

.apw-help {
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
    margin: 0 0 4px 0;
}

.apw-lock-note {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
    background: rgba(25, 118, 210, 0.06);
    border: 1px solid rgba(25, 118, 210, 0.25);
    border-radius: 6px;
    padding: 8px 12px;
    margin: 0;
}

.apw-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.apw-row {
    display: flex;
    gap: 16px;
}

.apw-row > .apw-field {
    flex: 1 1 0;
}

.apw-size-lock {
    align-self: flex-end;
    margin-bottom: 8px;
    font-size: 18px;
    color: var(--tf-fg-4, #6b7280);
}

.apw-size-lock--unknown {
    opacity: 0.5;
}

.apw-label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
    font-weight: 500;
}

.apw-req {
    color: #d32f2f;
    margin-left: 2px;
}

.apw-input {
    padding: 8px 12px;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
}

.apw-input:disabled {
    background: rgba(0, 0, 0, 0.03);
    cursor: not-allowed;
}

.apw-options {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    /* The list is its own scroll region: it never pushes the dialog taller. */
    max-height: min(340px, calc(92vh - 360px));
    min-height: 120px;
    overflow-y: auto;
    background: #fff;
}

.apw-option {
    padding: 8px 10px;
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.apw-option:last-child {
    border-bottom: 0;
}

.apw-option:hover,
.apw-option:focus {
    background: rgba(25, 118, 210, 0.08);
    outline: none;
}

.apw-option--selected {
    background: rgba(25, 118, 210, 0.16);
    font-weight: 600;
}

.apw-thumb {
    width: 36px;
    height: 36px;
    object-fit: contain;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.04);
}

.apw-thumb--placeholder {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(25, 118, 210, 0.18);
    color: #1976d2;
    font-weight: 600;
}

.apw-thumb--note {
    flex: 0 0 auto;
    font-size: 8px;
    line-height: 1.1;
    text-align: center;
    font-weight: 500;
}

.apw-option__name {
    flex: 1 1 auto;
}

.apw-option__badge {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.65);
}

.apw-empty {
    padding: 12px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 14px;
    border: 1px dashed rgba(0, 0, 0, 0.12);
    border-radius: 4px;
}

.apw-loading {
    padding: 12px;
    color: rgba(0, 0, 0, 0.55);
    font-size: 13px;
}

.apw-verdict {
    padding: 8px 12px;
    border-radius: 6px;
    background: rgba(76, 175, 80, 0.08);
    font-size: 14px;
}

.apw-verdict--remap {
    background: rgba(255, 152, 0, 0.12);
}

.apw-verdict--drop {
    background: rgba(244, 67, 54, 0.12);
}

.apw-verdict--unknown {
    background: rgba(0, 0, 0, 0.05);
}

.apw-verdict__label {
    font-weight: 600;
    margin-right: 6px;
}

.apw-error {
    color: #d32f2f;
    font-size: 12px;
    margin: 0;
}

.apw-error--submit {
    margin: 0 20px 12px 20px;
    font-size: 14px;
}

.apw-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.apw-btn {
    padding: 6px 14px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #fff;
    cursor: pointer;
    font-size: 14px;
}

.apw-btn--primary {
    background: #1976d2;
    color: #fff;
    border-color: #1976d2;
}

.apw-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* P5.5 PR-4: ghost variant for inline upload affordance */
.apw-btn--ghost {
    background: transparent;
    color: #1976d2;
    border: 1px dashed rgba(25, 118, 210, 0.5);
}

.apw-btn--ghost:hover {
    background: rgba(25, 118, 210, 0.06);
}

.apw-upload-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 4px;
}
</style>
